<script lang="ts">
  import { onMount } from 'svelte';
  import Navbar from '../components/Navbar.svelte';
  import { getBillingPlans, createSubscription, initiatePayment } from '../utils/billing';

  let plans = $state<any[]>([]);
  let loading = $state(true);
  let selectedPlan = $state<string | null>(null);
  let billingInterval = $state<'monthly' | 'yearly'>('monthly');
  let paymentMethod = $state<'mpesa' | 'paypal' | 'btc' | 'eth'>('mpesa');
  let phoneNumber = $state('');
  let processing = $state(false);
  let error = $state('');

  onMount(async () => {
    await loadPlans();
  });

  async function loadPlans() {
    try {
      const response = await getBillingPlans();
      plans = response.plans || [];
      console.log('Loaded plans:', plans);
      loading = false;
    } catch (e: any) {
      console.error('Failed to load plans:', e);
      error = 'Failed to load billing plans';
      loading = false;
    }
  }

  function handleSelectPlan(planId: string) {
    selectedPlan = planId;
    error = '';
  }

  async function handleCheckout() {
    if (!selectedPlan) {
      error = 'Please select a plan';
      return;
    }

    if (paymentMethod === 'mpesa' && !phoneNumber) {
      error = 'Please enter your M-PESA phone number';
      return;
    }

    try {
      processing = true;
      error = '';

      // Create subscription
      const subResponse = await createSubscription(selectedPlan, billingInterval);

      // Initiate payment
      const paymentDetails: Record<string, string> = {};
      if (paymentMethod === 'mpesa') {
        paymentDetails.phone_number = phoneNumber;
      }
      paymentDetails.payment_method = paymentMethod;

      const paymentResponse = await initiatePayment(
        subResponse.subscription.id,
        paymentMethod,
        paymentDetails,
        window.location.origin + '/profile?tab=billing'
      );

      // Handle different payment methods
      if (paymentResponse.checkoutUrl) {
        // Redirect for PayPal/Crypto
        window.location.href = paymentResponse.checkoutUrl;
      } else if (paymentResponse.paymentInstructions) {
        // Show instructions for Mpesa
        alert(paymentResponse.paymentInstructions);
        // Redirect to profile after showing instructions
        setTimeout(() => {
          window.location.href = '/profile?tab=billing';
        }, 2000);
      }
    } catch (e: any) {
      error = e.message || 'Payment failed. Please try again.';
      processing = false;
    }
  }

  function getPrice(plan: any) {
    return billingInterval === 'yearly' ? plan.priceYearly : plan.priceMonthly;
  }
</script>

<Navbar />

<div class="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4">
  <div class="max-w-7xl mx-auto">
    <div class="text-center mb-12">
      <h1 class="text-title-md font-bold text-gray-800 dark:text-white/90 mb-4">
        Flexible Plans Tailored to Fit Your Unique Needs!
      </h1>

      <!-- Billing interval toggle - TailAdmin design -->
      <div class="mt-8 flex items-center justify-center">
        <div class="relative z-1 inline-flex rounded-full bg-gray-200 dark:bg-gray-800 p-1">
          <!-- Sliding background -->
          <span
            class="absolute top-1/2 -z-1 flex h-11 w-[120px] -translate-y-1/2 rounded-full bg-white shadow-theme-xs duration-200 ease-linear dark:bg-white/10 {billingInterval === 'monthly' ? 'translate-x-0' : 'translate-x-full'}"
          ></span>
          <button
            onclick={() => billingInterval = 'monthly'}
            class="flex h-11 w-[120px] items-center justify-center text-base font-medium {billingInterval === 'monthly' ? 'text-gray-800 dark:text-white/90' : 'text-gray-500 hover:text-gray-700 dark:hover:text-white/70 dark:text-gray-400'}"
          >
            Monthly
          </button>
          <button
            onclick={() => billingInterval = 'yearly'}
            class="flex h-11 w-[120px] items-center justify-center text-base font-medium {billingInterval === 'yearly' ? 'text-gray-800 dark:text-white/90' : 'text-gray-500 hover:text-gray-700 dark:hover:text-white/80 dark:text-gray-400'}"
          >
            Annually
          </button>
        </div>
      </div>
    </div>

    {#if error}
      <div class="max-w-2xl mx-auto mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <p class="text-sm text-red-600 dark:text-red-400">{error}</p>
      </div>
    {/if}

    {#if loading}
      <div class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500 mb-4"></div>
        <p class="text-gray-600 dark:text-gray-400">Loading plans...</p>
      </div>
    {:else}
      <!-- Pricing cards grid - TailAdmin design -->
      {#if !selectedPlan}
      {#if plans.length === 0}
        <div class="text-center py-12">
          <svg class="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"/>
          </svg>
          <p class="text-lg text-gray-600 dark:text-gray-400">No billing plans available</p>
          <p class="text-sm text-gray-500 dark:text-gray-500 mt-2">Please contact support for more information</p>
        </div>
      {:else}
      <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 xl:gap-6 mb-12">
        {#each plans as plan, index}
          {@const isPro = plan.name === 'pro' || plan.name === 'medium'}
          <div
            onclick={() => handleSelectPlan(plan.id)}
            class="rounded-2xl p-6 cursor-pointer transition-all hover:shadow-xl {
              isPro
                ? 'border border-gray-800 bg-gray-800 dark:border-white/10 dark:bg-white/10'
                : 'border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]'
            } {selectedPlan === plan.id ? 'ring-2 ring-brand-500 scale-105' : ''}"
          >
            <span class="mb-3 block text-theme-xl font-semibold {isPro ? 'text-white' : 'text-gray-800 dark:text-white/90'}">
              {plan.displayName || plan.name || 'Unknown Plan'}
            </span>

            <div class="mb-1 flex items-center justify-between">
              <div class="flex items-end">
                <h2 class="text-title-md font-bold {isPro ? 'text-white' : 'text-gray-800 dark:text-white/90'}">
                  {#if plan.name === 'free' || (!plan.priceMonthly && !plan.priceYearly)}
                    Free
                  {:else}
                    ${(getPrice(plan) || 0).toFixed(2)}
                  {/if}
                </h2>
                {#if plan.name !== 'free' && (plan.priceMonthly || plan.priceYearly)}
                  <span class="mb-1 inline-block text-sm {isPro ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}">
                    /month
                  </span>
                {/if}
              </div>
              {#if plan.name !== 'free' && (plan.priceMonthly || plan.priceYearly)}
                <span class="text-theme-xl font-semibold {isPro ? 'text-gray-300' : 'text-gray-400'} line-through">
                  ${billingInterval === 'monthly' ? ((getPrice(plan) || 0) * 2.4).toFixed(2) : ((getPrice(plan) || 0) * 3.75).toFixed(2)}
                </span>
              {/if}
            </div>

            <p class="text-sm {isPro ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}">
              {plan.description || (plan.name === 'free' ? 'Perfect for getting started' : plan.name === 'pro' || plan.name === 'medium' ? 'For working on commercial projects' : 'For large-scale operations')}
            </p>

            <div class="my-6 h-px w-full {isPro ? 'bg-white/20' : 'bg-gray-200 dark:bg-gray-800'}"></div>

            <div class="mb-8 space-y-3">
              {#if plan.features && plan.features.length > 0}
                {#each plan.features as feature}
                  <p class="flex items-center gap-3 text-sm {isPro ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13.4017 4.35986L6.12166 11.6399L2.59833 8.11657"
                        stroke="#12B76A"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    {feature}
                  </p>
                {/each}
              {:else}
                <p class="text-sm {isPro ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}">
                  No features listed
                </p>
              {/if}
            </div>

            <button
              onclick={() => handleSelectPlan(plan.id)}
              class="flex w-full items-center justify-center rounded-lg p-3.5 text-sm font-medium shadow-theme-xs transition-colors {
                isPro
                  ? 'bg-brand-500 hover:bg-brand-600 text-white'
                  : 'bg-gray-800 hover:bg-brand-500 text-white dark:bg-white/10'
              } {selectedPlan === plan.id ? 'ring-2 ring-brand-400 scale-105' : ''}"
            >
              {selectedPlan === plan.id ? 'Selected' : `Choose ${plan.displayName}`}
            </button>
          </div>
        {/each}
      </div>
      {/if}
      {/if}

      <!-- Payment section - improved layout -->
      {#if selectedPlan}
        {@const selectedPlanData = plans.find(p => p.id === selectedPlan)}
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-4xl mx-auto border border-gray-200 dark:border-gray-800">
          <!-- Back to plans button -->
          <button
            onclick={() => selectedPlan = null}
            class="mb-6 flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:text-brand-500 dark:hover:text-brand-400 transition-colors"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
            </svg>
            <span class="font-medium">Back to Plans</span>
          </button>

          <div class="flex items-center justify-between mb-6 pb-6 border-b border-gray-300 dark:border-gray-700">
            <h2 class="text-3xl font-bold text-gray-900 dark:text-white">
              Complete Your Purchase
            </h2>
          </div>

          <!-- Order Summary -->
          {#if selectedPlanData}
            <div class="bg-white dark:bg-gray-800 rounded-xl p-6 mb-8 border border-gray-200 dark:border-gray-700">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Order Summary</h3>
              <div class="flex justify-between items-center mb-3">
                <span class="text-gray-700 dark:text-gray-300">{selectedPlanData.displayName} Plan</span>
                <span class="text-gray-900 dark:text-white font-semibold">
                  {#if selectedPlanData.name === 'free'}
                    Free
                  {:else}
                    ${getPrice(selectedPlanData).toFixed(2)}
                  {/if}
                </span>
              </div>
              <div class="flex justify-between items-center text-sm text-gray-600 dark:text-gray-400 mb-4">
                <span>Billing Period</span>
                <span class="capitalize">{billingInterval}</span>
              </div>
              <div class="border-t border-gray-200 dark:border-gray-700 pt-3 flex justify-between items-center">
                <span class="text-lg font-bold text-gray-900 dark:text-white">Total</span>
                <span class="text-2xl font-bold text-brand-500">
                  {#if selectedPlanData.name === 'free'}
                    $0.00
                  {:else}
                    ${getPrice(selectedPlanData).toFixed(2)}
                  {/if}
                </span>
              </div>
            </div>
          {/if}

          {#if selectedPlanData?.name === 'free'}
            <!-- Free plan activation -->
            <div class="text-center py-8">
              <div class="mb-6">
                <svg class="w-20 h-20 mx-auto text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>
              </div>
              <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-3">Start for Free!</h3>
              <p class="text-gray-600 dark:text-gray-400 mb-6">No payment required. Get started immediately.</p>
              <a
                href="/profile"
                class="inline-block px-8 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors text-lg"
              >
                Get Started Now
              </a>
            </div>
          {:else}
            <!-- Payment method selection -->
            <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">Select Payment Method</h3>

            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <button
                onclick={() => paymentMethod = 'mpesa'}
                class="p-4 border-2 rounded-xl transition-all {paymentMethod === 'mpesa' ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20 shadow-lg scale-105' : 'border-gray-300 dark:border-gray-600 hover:border-brand-400 hover:shadow-md'}"
              >
                <div class="text-lg font-semibold text-gray-900 dark:text-white">M-PESA</div>
                <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">Mobile Money</div>
              </button>
              <button
                onclick={() => paymentMethod = 'paypal'}
                class="p-4 border-2 rounded-xl transition-all {paymentMethod === 'paypal' ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20 shadow-lg scale-105' : 'border-gray-300 dark:border-gray-600 hover:border-brand-400 hover:shadow-md'}"
              >
                <div class="text-lg font-semibold text-gray-900 dark:text-white">PayPal</div>
                <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">Cards & PayPal</div>
              </button>
              <button
                onclick={() => paymentMethod = 'btc'}
                class="p-4 border-2 rounded-xl transition-all {paymentMethod === 'btc' ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20 shadow-lg scale-105' : 'border-gray-300 dark:border-gray-600 hover:border-brand-400 hover:shadow-md'}"
              >
                <div class="text-lg font-semibold text-gray-900 dark:text-white">Bitcoin</div>
                <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">BTC</div>
              </button>
              <button
                onclick={() => paymentMethod = 'eth'}
                class="p-4 border-2 rounded-xl transition-all {paymentMethod === 'eth' ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20 shadow-lg scale-105' : 'border-gray-300 dark:border-gray-600 hover:border-brand-400 hover:shadow-md'}"
              >
                <div class="text-lg font-semibold text-gray-900 dark:text-white">Ethereum</div>
                <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">ETH/USDC/USDT</div>
              </button>
            </div>

            {#if paymentMethod === 'mpesa'}
              <div class="mb-6 bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  M-PESA Phone Number
                </label>
                <input
                  type="tel"
                  bind:value={phoneNumber}
                  placeholder="254712345678"
                  class="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-all"
                />
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Enter your phone number in format: 254XXXXXXXXX
                </p>
              </div>
            {/if}

            <button
              onclick={handleCheckout}
              disabled={processing || (paymentMethod === 'mpesa' && !phoneNumber)}
              class="w-full px-6 py-4 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-xl font-semibold text-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-green-600"
            >
              {#if processing}
                <span class="flex items-center justify-center gap-2">
                  <div class="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Processing Payment...
                </span>
              {:else}
                <span class="flex items-center justify-center gap-2">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                  </svg>
                  Proceed to Secure Payment
                </span>
              {/if}
            </button>

            <p class="text-xs text-gray-500 dark:text-gray-400 text-center mt-4">
              🔒 Secure payment. By completing your purchase, you agree to our
              <a href="/terms" class="text-brand-500 hover:underline">Terms of Service</a> and
              <a href="/privacy" class="text-brand-500 hover:underline">Privacy Policy</a>
            </p>
          {/if}
        </div>
      {/if}
    {/if}
  </div>
</div>
